import React from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { useFormik } from 'formik';
import {
  Checkbox,
  Select,
  Multiselect,
  TextField,
  Section,
} from 'components/shared';
import { ResellerForm } from './ResellerForm';
import { Button, Stack } from '@mui/material';
import { AddressForm } from './AddressForm';
import { objectAttribute } from 'utils/resources/ObjectAttributes';
import FeatureFlagsContext from '../FeatureFlagsContext';

export default function PracticeInfo(props) {
  const { organization } = props;

  const featureFlags = React.useContext(FeatureFlagsContext);

  const [businessStructureOptions, setBusinessStructureOptions] =
    React.useState([]);
  const [practiceTypeOptions, setPracticeTypeOptions] = React.useState([]);

  const formik = useFormik({
    initialValues: {
      ...props.initialFormState,
      mailingCountry:
        props.initialFormState.mailingCountry ||
        props.initialFormState.billingCountry,
    },
  });
  const [validationErrors, setValidationErrors] = React.useState(
    props.initialValidationErrors || {}
  );

  const [attributesBeingUpdated, setAttributesBeingUpdated] = React.useState(
    []
  );

  const hasInvalidValue = (attribute) =>
    validationErrors.hasOwnProperty(attribute);
  const showErrors = (attribute) =>
    !!formik.touched[attribute] &&
    hasInvalidValue(attribute) &&
    !attributesBeingUpdated.includes(attribute);
  const errorText = (attribute) =>
    showErrors(attribute) ? validationErrors[attribute].join(' ') : '';

  const [loading, setLoading] = React.useState(false);

  const saveForm = (attributes) => {
    return props.onSave(attributes).then(({ validationErrors }) => {
      setValidationErrors(validationErrors);
      return Promise.resolve({ validationErrors });
    });
  };

  const persistFormState = React.useMemo(
    () => debounce(saveForm, 300, { leading: true }),
    []
  );

  React.useEffect(() => {
    Promise.all([
      objectAttribute({ objectType: 'practice', attribute: 'business_types' }),
      objectAttribute({ objectType: 'practice', attribute: 'practice_types' }),
    ]).then(([currentBusinessStructureOptions, currentPracticeTypeOptions]) => {
      setBusinessStructureOptions(currentBusinessStructureOptions);
      setPracticeTypeOptions(currentPracticeTypeOptions);
    });
  }, []);

  const UsOrCanadaIsBillingCountry = ['United States', 'Canada'].includes(
    formik.values.billingCountry
  );

  React.useEffect(() => {
    // Change mailing address when isMailingAdressDifferent is toggled
    // It will either be cleared or set to the billing address

    if (
      formik.values.isMailingAddressDifferent ===
      props.initialFormState.isMailingAddressDifferent
    )
      return;

    const mailingAddressAttributes = (isMailingAddressDifferent) => {
      if (isMailingAddressDifferent) {
        return {
          mailingAddress1: '',
          mailingAddress2: '',
          mailingCity: '',
          mailingState: '',
          mailingZip: '',
          mailingCountry: formik.values.billingCountry,
        };
      } else {
        return {
          mailingAddress1: formik.values.billingAddress1,
          mailingAddress2: formik.values.billingAddress2,
          mailingCity: formik.values.billingCity,
          mailingState: formik.values.billingState,
          mailingZip: formik.values.billingZip,
          mailingCountry: formik.values.billingCountry,
        };
      }
    };

    const formData = {
      ...formik.values,
      ...mailingAddressAttributes(formik.values.isMailingAddressDifferent),
    };
    formik.setValues(formData).then(() => saveForm(formData));
  }, [formik.values.isMailingAddressDifferent]);

  React.useEffect(() => {
    // Sync billing address form updates to mailing address values when they're the same (ie !isMailingAddressDifferent)

    if (!formik.values.isMailingAddressDifferent) {
      const formikValues = {
        ...formik.values,
        mailingAddress1: formik.values.billingAddress1,
        mailingAddress2: formik.values.billingAddress2,
        mailingCity: formik.values.billingCity,
        mailingState: formik.values.billingState,
        mailingZip: formik.values.billingZip,
        mailingCountry: formik.values.billingCountry,
      };
      formik.setValues(formikValues).then(() => {
        persistFormState(formikValues);
      });
    }
  }, [
    formik.values.billingAddress1,
    formik.values.billingAddress2,
    formik.values.billingCity,
    formik.values.billingState,
    formik.values.billingZip,
    formik.values.billingCountry,
  ]);

  const handleSubmit = (e) => {
    e.preventDefault();

    saveForm(formik.values).then(
      ({ validationErrors: latestValidationErrors }) => {
        const touched = Object.keys(formik.values).reduce((touched, key) => {
          touched[key] = true;
          return touched;
        }, {});

        formik.setTouched(touched).then(() => {
          if (Object.keys(latestValidationErrors).length === 0) {
            props.goToNextPage();
          }
        });
      }
    );
  };

  const handleChange = (e) => {
    formik.handleChange(e);
    const attribute = e.target.name;

    setAttributesBeingUpdated([...attributesBeingUpdated, attribute]);
    persistFormState({ ...formik.values, [attribute]: e.target.value }).then(
      () => {
        setAttributesBeingUpdated(
          [...attributesBeingUpdated].filter((attr) => attr !== attribute)
        );
      }
    );
  };

  const handleMultiSelectChange = (name, value) => {
    setAttributesBeingUpdated([...attributesBeingUpdated, name]);

    formik
      .setFieldValue(name, value)
      .then(() => saveForm({ ...formik.values, [name]: value }))
      .then(() => {
        setAttributesBeingUpdated(
          [...attributesBeingUpdated].filter((attr) => attr !== name)
        );
      });
  };

  const handleBlur = (e) => formik.handleBlur(e);

  const handleMailingAddressCheckboxChange = (e) => {
    formik
      .setFieldValue('isMailingAddressDifferent', e.target.checked)
      .then((response) => {});
  };

  const orgName = organization.attributes.name;
  const orgEmail = organization.attributes.email;

  const allowProgress =
    Object.keys(validationErrors).length === 0 &&
    ['United States', 'Canada'].includes(formik.values.billingCountry);

  const billingCountryOptions = [
    'United States',
    'Canada',
    'Australia',
    'Other',
  ];

  const mailingCountryOptions = UsOrCanadaIsBillingCountry
    ? ['United States', 'Canada']
    : [];

  const disableIsMailingAddressDifferent = !UsOrCanadaIsBillingCountry;

  return (
    <ResellerForm
      tab="practiceInfo"
      location={props.location}
      allowProgress={allowProgress}
      loading={loading}
    >
      <form noValidate onSubmit={handleSubmit}>
        <Stack spacing={5.5}>
          <Section
            title="CONTACT INFO"
            components={[
              <TextField
                sectionComponentSize="large"
                name="name"
                variant="outlined"
                label="Practice Name"
                required
                errorMessage={errorText('name')}
                error={showErrors('name')}
                value={formik.values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={orgName ? true : false}
              />,
              <TextField
                name="phone"
                variant="outlined"
                label="Practice Phone"
                required
                phoneNumberMask
                errorMessage={errorText('phone')}
                error={showErrors('phone')}
                value={formik.values.phone}
                onChange={handleChange}
                onBlur={handleBlur}
              />,
              <TextField
                name="email"
                variant="outlined"
                label="Practice Email"
                required
                errorMessage={errorText('email')}
                error={showErrors('email')}
                value={orgEmail || formik.values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={orgEmail ? true : false}
              />,
            ]}
          />
          <Section
            title="PRACTICE INFO"
            components={[
              <Select
                id="practice_info_practice_species"
                name="practiceSpecies"
                value={formik.values.practiceSpecies}
                errorMessage={errorText('practiceSpecies')}
                onBlur={handleBlur}
                error={showErrors('practiceSpecies')}
                label="Practice Species"
                required
                onChange={(e) => handleChange(e)}
                needsDefaultOption
                options={[
                  { value: 'large animal', label: 'Large Animal' },
                  { value: 'mixed animal', label: 'Mixed Animal' },
                  { value: 'small animal', label: 'Small Animal' },
                ]}
              />,
              <Multiselect
                label="Practice Type"
                required
                id="practice_info_practice_type"
                name="practiceType"
                value={formik.values.practiceType}
                errorMessage={errorText('practiceType')}
                error={showErrors('practiceType')}
                onChange={handleMultiSelectChange}
                onBlur={handleBlur}
                options={practiceTypeOptions}
              />,
              <Select
                id="practice_info_business_structure"
                name="businessStructure"
                value={formik.values.businessStructure}
                errorMessage={errorText('businessStructure')}
                error={showErrors('practiceType')}
                label="Business Structure"
                onBlur={handleBlur}
                needsDefaultOption
                required
                onChange={handleChange}
                options={businessStructureOptions}
              />,
              <Select
                id="practice_info_language"
                name="language"
                value={formik.values.language}
                errorMessage={errorText('language')}
                error={showErrors('language')}
                label="Language Preference"
                required
                onBlur={handleBlur}
                onChange={handleChange}
                options={[
                  { value: 'english', label: 'English' },
                  { value: 'french', label: 'French' },
                ]}
              />,
            ]}
          />
          <Section
            title="BILLING ADDRESS"
            components={[
              <AddressForm
                sectionComponentSize="large"
                onChange={handleChange}
                onBlur={handleBlur}
                errorText={errorText}
                addressType="billing"
                validationErrors={validationErrors}
                countryOptions={billingCountryOptions}
                country={formik.values.billingCountry}
                address1={formik.values.billingAddress1}
                address2={formik.values.billingAddress2}
                city={formik.values.billingCity}
                state={formik.values.billingState.toUpperCase()}
                zip={formik.values.billingZip}
              />,
            ]}
          />
          {
            <Checkbox
              disabled={disableIsMailingAddressDifferent}
              name="isMailingAddressDifferent"
              color="primary"
              value={formik.values.isMailingAddressDifferent}
              onChange={handleMailingAddressCheckboxChange}
              label="Mailing Address (e.g. for catalogs & magazines) different from Billing Address?"
              labelPlacement="end"
            />
          }
          {formik.values.isMailingAddressDifferent && (
            <Section
              title="MAILING ADDRESS"
              components={[
                <AddressForm
                  // Having different mailing and billing countries is only supported for US and Canada
                  // Therefore we don't need to show the country dropdown if the billing country is not US or Canada
                  hideCountry={!UsOrCanadaIsBillingCountry}
                  // If the US or Canada is the billing country we want to only have US and Canada as options for the mailing country
                  countryOptions={mailingCountryOptions}
                  country={formik.values.mailingCountry}
                  address1={formik.values.mailingAddress1}
                  address2={formik.values.mailingAddress2}
                  city={formik.values.mailingCity}
                  state={formik.values.mailingState}
                  zip={formik.values.mailingZip}
                  sectionComponentSize="large"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorText={errorText}
                  addressType="mailing"
                  validationErrors={validationErrors}
                />,
              ]}
            />
          )}
          <Section
            components={[
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                onClick={props.goToPreviousPage}
              >
                BACK
              </Button>,
              <Button
                disabled={!allowProgress}
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleSubmit}
              >
                NEXT
              </Button>,
            ]}
          />
        </Stack>
      </form>
    </ResellerForm>
  );
}
